import { useState, useEffect, useCallback } from 'react';
import { safeGet, safeSet } from '../../utils/safeStorage';
import { STORAGE_KEYS } from './storageKeys';

const ISSUE_LABELS = {
  fraud: 'Fraud control answered No — countermeasure required',
  pcc: 'PCC question answered No/NA — remediation required',
  dlc: 'DLC declaration flagged a governance gap',
  dlc_applicability: 'DLC declared N/A with deficiency — applicability review',
  dlc_exception: 'DLC informational exception (N/G, no deficiency)',
};

const APPROVAL_FLOW = {
  'review':            { next: 'UnderReview',   label: 'Review started' },
  'approve-division':  { next: 'Approved',      label: 'Division approved' },
  'approve-final':     { next: 'FinalApproved', label: 'Final approved' },
  'reject':            { next: 'Rejected',      label: 'Rejected' },
};

export function useFormResponsesState({ user, addCountermeasure, updateRisk, addNotification }) {
  const [formResponses, setFormResponses] = useState(() => safeGet(STORAGE_KEYS.responses, {}));

  useEffect(() => { safeSet(STORAGE_KEYS.responses, formResponses); }, [formResponses]);

  const submitFormResponse = useCallback((moduleKey, recordId, values) => {
    const key = `${moduleKey}:${recordId}`;
    const rt = (values?.responseType || '').trim();
    const deficiency = (values?.deficiency || '').trim();

    // Categorize DLC outcome (used by dashboards + persisted on the response)
    let dlcOutcome = null;
    if (moduleKey === 'dlc') {
      if (rt === 'OK' || rt === 'Yes') dlcOutcome = 'ok';
      else if (rt === 'N/G' && deficiency === 'Yes') dlcOutcome = 'ng-gap';        // governance gap
      else if (rt === 'N/G' && deficiency === 'No')  dlcOutcome = 'ng-exception';  // informational
      else if (rt === 'N/A' && deficiency === 'Yes') dlcOutcome = 'na-review';     // applicability review
      else if (rt === 'N/A' && deficiency === 'No')  dlcOutcome = 'na-valid';      // valid non-applicable
    }

    setFormResponses((r) => ({
      ...r,
      [key]: {
        moduleKey,
        recordId,
        values,
        dlcOutcome,
        submittedAt: new Date().toISOString(),
        submittedBy: user?.name || 'Demo User',
        submittedRole: user?.role || 'demo',
        submittedDepartment: user?.department || null,
        workflowStatus: values?.workflowStatus || 'Submitted',
        history: (values?.history || []).concat([{
          action: 'Submitted', by: user?.name || 'Demo User', role: user?.role, at: new Date().toISOString(),
        }]),
      },
    }));

    // ---- Auto-task creation, branched per module + DLC 3-level rules ----
    const baseCm = {
      relatedId: recordId,
      riskId: values?.linkedRiskId || null,
      department: user?.department || values?.assignedDepartment || null,
      owner: values?.owner || user?.name || 'Department Head',
    };

    if (moduleKey === 'dlc') {
      // Per HMSI spec: do NOT treat N/G or N/A as automatic failure.
      if (dlcOutcome === 'ng-gap') {
        addCountermeasure({
          ...baseCm,
          module: 'DLC',
          taskType: 'Governance Gap',
          issue: values?.currentSituation || ISSUE_LABELS.dlc,
        });
      } else if (dlcOutcome === 'na-review') {
        addCountermeasure({
          ...baseCm,
          module: 'DLC',
          taskType: 'Applicability Review',
          issue: values?.currentSituation || ISSUE_LABELS.dlc_applicability,
        });
      }
      // ng-exception and na-valid: no countermeasure created (logged on response only)
    } else if (['fraud', 'pcc'].includes(moduleKey) && ['No', 'N/A'].includes(rt)) {
      addCountermeasure({
        ...baseCm,
        module: moduleKey.toUpperCase(),
        issue: values?.currentSituation || ISSUE_LABELS[moduleKey],
      });
      // Flag related risk: bump residual via control reduction for R20 if PCC B4 = No
      if (moduleKey === 'pcc' && values?.linkedRiskId === 'R20' && rt === 'No') {
        updateRisk('R20', { controlMeasure: 1 });
      }
    }
  }, [user, addCountermeasure, updateRisk]);

  // Approval workflow: Dept Head → Division Head → Operating Head
  const advanceApproval = useCallback((responseKey, action, comment = '') => {
    const cur = formResponses[responseKey];
    if (!cur) return;
    const spec = APPROVAL_FLOW[action];
    if (!spec) return;

    const newEntry = {
      action: spec.label, by: user?.name || 'Reviewer', role: user?.role, comment, at: new Date().toISOString(),
    };

    setFormResponses((r) => {
      const existing = r[responseKey];
      if (!existing) return r;
      return {
        ...r,
        [responseKey]: {
          ...existing,
          workflowStatus: spec.next,
          history: [...(existing.history || []), newEntry],
        },
      };
    });

    // Side-effects kept OUT of the setState updater (StrictMode double-invokes updaters)
    const moduleKey = cur.moduleKey?.toUpperCase();
    const dept = cur.submittedDepartment;
    const meta = { responseKey, moduleKey: cur.moduleKey, recordId: cur.recordId, department: dept, comment };

    if (action === 'approve-division') {
      addNotification({
        type: 'handoff',
        targetRole: 'operating_head',
        title: 'New response awaiting final approval',
        body: `${user?.name || 'Division Head'} forwarded ${moduleKey} ${cur.recordId} (${dept || '—'}) for your final sign-off.`,
        link: '/approvals',
        meta,
      });
    } else if (action === 'approve-final') {
      addNotification({
        type: 'approved',
        targetRole: 'department_head',
        targetDepartment: dept,
        title: `${moduleKey} ${cur.recordId} fully approved`,
        body: `${user?.name || 'Operating Head'} granted final approval${comment ? ` — "${comment}"` : '.'}`,
        link: '/my-tasks',
        meta,
      });
    } else if (action === 'reject') {
      addNotification({
        type: 'rejected',
        targetRole: 'department_head',
        targetDepartment: dept,
        title: `${moduleKey} ${cur.recordId} rejected`,
        body: `${user?.name || 'Reviewer'} requested rework${comment ? ` — "${comment}"` : '.'}`,
        link: '/my-tasks',
        meta,
      });
    }
  }, [user, addNotification, formResponses]);

  return { formResponses, submitFormResponse, advanceApproval, setFormResponses };
}
